import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DraftList } from 'app/application/interfaces/draft';
import { AuthService } from 'app/auth/auth.service';
import { API_ENDPOINTS } from 'app/core/constants/api-endpoints.constant';
import { EncryptionService } from 'app/services/encryption.service';
import { ApiService } from 'app/shared/constant/api-service/api.service';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class DashboardService {
    selectedLanguage: string = 'bm';
    private _language$ = new BehaviorSubject<string>(
        localStorage.getItem('language') || 'en'
    );
    language$ = this._language$.asObservable();

    loggedInUserData: any;

    private loggedInUserSubject = new BehaviorSubject<any>(null);

    private profileImageURLSubject = new BehaviorSubject<string | null>(null);

    constructor(
        private authService: AuthService,
        private encryptionService: EncryptionService,
        private toastr: ToastrService,
        private _apiService: ApiService
    ) {
        this.selectedLanguage = localStorage.getItem('language') || 'en';
        const encryptionKey = atob(localStorage.getItem('cepn'));
        const userDataTemp = this.encryptionService.decryptData(
            localStorage.getItem('profile'),
            encryptionKey
        );

        const userData = userDataTemp === '' ? null : atob(userDataTemp);

        this.loggedInUserData = userData
            ? JSON.parse(userData)
            : { username: '', phoneNumber: '', email: '' };

        if (!userData) {
            this.toastr.error('⚠️ something went wrong please relogin!');
            this.authService.signOut();
        }

        this.loggedInUserSubject.next(this.loggedInUserData);
        this.watchProfileChanges();

        this.fetchProfileImageData();
    }

    getUser(): any {
        return this.loggedInUserData;
    }

    private watchProfileChanges(): void {
        let lastProfile = localStorage.getItem('profile');

        setInterval(() => {
            const currentProfile = localStorage.getItem('profile');

            if (currentProfile && currentProfile !== lastProfile) {
                lastProfile = currentProfile;

                try {
                    const encryptionKey = atob(localStorage.getItem('cepn'));
                    const decrypted = this.encryptionService.decryptData(
                        currentProfile,
                        encryptionKey
                    );

                    if (!decrypted) return;

                    const parsedUser = JSON.parse(atob(decrypted));

                    this.loggedInUserData = parsedUser;
                    this.loggedInUserSubject.next(parsedUser);
                } catch {}
            }
        }, 1000);
    }

    getSelectedLanguage(): string {
        return this.selectedLanguage;
    }

    setLanguage(lang: string) {
        localStorage.setItem('language', lang);
        localStorage.setItem('switched_language', lang);
        this._language$.next(lang);
    }

    getProfileCompletion(
        user: any,
        url: string
    ): {
        completion: number;
        isProfileDetailsComplete: boolean;
        isBusinessDetailsComplete: boolean;
    } {
        const profileFields = [
            user?.username,
            user?.email,
            user?.phoneNumber,
            user?.details?.gender,
            user?.details?.fullname,
        ];

        const businessFields = [
            user?.details?.bizname,
            user?.details?.address,
            user?.details?.companycontactdetails,
            user?.details?.companyemail,
            user?.details?.testconsttype,
            user?.details?.sabah,
            user?.details?.entitytype,
            user?.details?.yearsinoperation,
            user?.details?.fulltimeemployees,
        ];

        const isProfileDetailsComplete = profileFields.every(Boolean);
        const isBusinessDetailsComplete = businessFields.every(Boolean);
        const hasProfilePic = !!url;

        let completion = 0;
        if (isProfileDetailsComplete) completion += 30;
        if (isBusinessDetailsComplete) completion += 50;
        if (hasProfilePic) completion += 20;

        return {
            completion,
            isProfileDetailsComplete,
            isBusinessDetailsComplete,
        };
    }

    getApplications(source?): Observable<any> {
        let url = API_ENDPOINTS.GET_CASE_STATUS;
        if (source) url += `?source=${source}`;
        return this._apiService.get(url).pipe(catchError(this.handleError));
    }

    getApplicationDetails(caseId): Observable<any> {
        return this._apiService
            .get(`${API_ENDPOINTS.GET_CASE_DETAIL}?caseId=${caseId}`)
            .pipe(catchError(this.handleError));
    }

    getOldCaseStatus(): Observable<any> {
        return this._apiService
            .get(`${API_ENDPOINTS.GET_CASE_STATUS}?oldcase`)
            .pipe(catchError(this.handleError));
    }

    getDrafts(): Observable<DraftList> {
        return this._apiService
            .get(API_ENDPOINTS.GET_DRAFTS)
            .pipe(catchError(this.handleError));
    }

    deleteDrafts(data: any): Observable<string> {
        return this._apiService
            .post(API_ENDPOINTS.DELETE_DRAFTS, data)
            .pipe(catchError(this.handleError));
    }

    uploadProfileImage(
        formData: FormData,
        headers: { [header: string]: string }
    ): Observable<any> {
        return this._apiService.post(
            API_ENDPOINTS.UPLOAD_PROFILE_PIC,
            formData,
            { headers: new HttpHeaders(headers) }
        );
    }

    fetchProfileImageData(): void {
        const headers = new HttpHeaders({
            pooltype: 'user',
            'Content-Type': 'application/json',
        });

        const body = {
            alias: 'PROFILE',
            downloadType: 'URL',
        };

        this._apiService
            .post(API_ENDPOINTS.GET_PROFILE_PIC, body, { headers })
            .pipe(
                tap((response: any) => {
                    this.profileImageURLSubject.next(response?.URL || null);
                })
            )
            .subscribe({
                error: () => this.profileImageURLSubject.next(null),
            });
    }

    getProfileImageUrl$(): Observable<string | null> {
        return this.profileImageURLSubject.asObservable();
    }

    handleError(error: HttpErrorResponse): Observable<any> {
        if (error.error instanceof ErrorEvent)
            return throwError('Network Error');
        if (error.status === 404) return throwError('Not Found');
        if (error.status === 400) return throwError('Bad Request');
        return throwError('Something went wrong please try again');
    }
}
